import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../components/AppShell';
import { eligibilityApi, BAND_STYLE, shortDate } from '../lib/eligibilityApi';
import type { ExamEligibility, EligibilityStudent, EligibilityClass } from '../lib/eligibilityApi';
import { StatTile } from '../components/charts';

/**
 * WHO WILL NOT BE ALLOWED TO SIT THE EXAM — and who can still be saved.
 *
 * ⚠ THE SCREEN LEADS WITH "CANNOT RECOVER", NOT WITH A PERCENTAGE. "23 students are short" is a
 * report and a principal has seen a thousand of them. "6 of these cannot reach 75% before the 3rd
 * of September however perfectly they attend from tomorrow, and the other 17 can" is a decision
 * with a deadline. That reordering is the entire feature; everything else on the page is support.
 *
 * ⚠ THERE IS NO EDIT CONTROL ON THIS PAGE AND THERE MUST NOT BE ONE. The ask was for the principal
 * to phone the teacher and get attendance "adjusted" from here. CBSE inspects attendance records,
 * forbids changes once shortage cases have been submitted, and lists disaffiliation among the
 * penalties for discrepancies. So this page identifies and routes — name, class, class teacher, one
 * tap to call — and the correction happens in the register where it is attributed and audited. The
 * page says so out loud, because that sentence is a selling point rather than a limitation.
 *
 * ⚠ The recovery figure is the OPTIMISTIC bound — perfect attendance from tomorrow. Said on screen,
 * because a bound read as a forecast is worse than no bound at all.
 */
export function ExamEligibilityPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState<ExamEligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [only, setOnly] = useState<'watch' | 'stuck' | 'cie'>('watch');

  const institutionId = params.get('institution') ?? '';
  const cutOff = params.get('cutOff') ?? '';
  const excludeExamDays = params.get('examDays') !== 'in';

  // Mirrors the backend `hr` policy; the catch below covers it drifting.
  const canSeeContact = !!user && (ROLES.hr as readonly string[]).includes(user.role);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const q = { institutionId: institutionId || null, cutOff: cutOff || null, excludeExamDays };
    eligibilityApi
      .watchlist({ ...q, withContact: canSeeContact })
      .catch(() => (canSeeContact ? eligibilityApi.watchlist(q) : Promise.reject(new Error('no'))))
      .then((r) => {
        if (!alive) return;
        setD(r);
        setFailed(false);
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [institutionId, cutOff, excludeExamDays, canSeeContact]);

  function set(k: string, v: string | null) {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  }

  if (loading && !d) return <p style={{ color: 'var(--ink-3)' }}>Working through the registers…</p>;
  if (failed || !d) {
    return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load eligibility.</div>;
  }

  const stuck = d.watchlist.filter((s) => !s.canStillRecover);
  const cie = d.watchlist.filter((s) => s.cieShortfallSubjects.length > 0);
  const shown = only === 'stuck' ? stuck : only === 'cie' ? cie : d.watchlist;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Academics</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Exam eligibility</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '76ch' }}>
          Who is at risk of being barred from the next examination on attendance — and, for each of
          them, whether there is still time to fix it.
        </p>
      </div>

      {/* ---------------------------- controls ---------------------------- */}
      <div className="tab-strip elig-controls" style={{ marginBottom: 12, borderBottom: 'none' }}>
        {d.institutions.map((i) => (
          <button
            key={i.id}
            type="button"
            className={'chip' + (i.id === d.institutionId ? ' is-on' : '')}
            onClick={() => set('institution', i.id)}
          >
            {i.name}
            <span style={{ color: 'var(--ink-3)' }}> · {i.board === 'Unspecified' ? i.kind : i.board}</span>
          </button>
        ))}
        <label style={{ fontSize: 12.5, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
          Cut-off
          <input
            type="date"
            className="input"
            style={{ width: 'auto' }}
            value={d.cutOff}
            onChange={(e) => set('cutOff', e.target.value || null)}
          />
        </label>
        <label style={{ fontSize: 12.5, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={d.excludeExamDays}
            onChange={(e) => set('examDays', e.target.checked ? null : 'in')}
          />
          Exclude exam days
        </label>
      </div>

      {/* ---------------------------- the rule ---------------------------- */}
      <div className="card elig-rule" style={{ padding: '12px 15px', marginBottom: 14 }}>
        <strong style={{ fontSize: 13 }}>{d.ruleLabel}.</strong>{' '}
        <span style={{ color: 'var(--ink-2)', fontSize: 12.5 }}>
          Measured to <strong>{shortDate(d.cutOff)}</strong> ({d.cutOffLabel}), with{' '}
          <strong>{d.teachingDaysLeft}</strong> teaching day{d.teachingDaysLeft === 1 ? '' : 's'} left.
          {d.excludeExamDays && d.examDaysExcluded > 0 &&
            ` ${d.examDaysExcluded} examination date${d.examDaysExcluded === 1 ? '' : 's'} in this window are not counted as instruction days.`}
          {d.usesPeriodAttendance
            ? ' Counting per-period attendance.'
            : ' Counting daily attendance.'}
        </span>
      </div>

      {d.note && (
        <div className="card" style={{
          padding: '11px 14px', marginBottom: 14, fontSize: 12.5, lineHeight: 1.6,
          color: 'var(--ink-2)', background: 'var(--surface-2)',
        }}>⚠ {d.note}</div>
      )}

      <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
        <div className="elig-tiles" style={{ marginBottom: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <StatTile label="Clear" value={String(d.eligible)} foot={`of ${d.students} on the roll`} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <StatTile label="Needs condonation" value={String(d.condonation)}
              foot={`between ${d.condonationFloor}% and ${d.eligibleThreshold}%`} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <StatTile label="Below the floor" value={String(d.detained)}
              foot={<span style={{ color: d.detained > 0 ? 'var(--danger)' : undefined }}>
                under {d.condonationFloor}%
              </span>} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <StatTile label="Cannot recover" value={String(d.unrecoverable)}
              foot={<span style={{ color: d.unrecoverable > 0 ? 'var(--danger)' : undefined }}>
                {d.unrecoverable > 0 ? 'needs a decision, not more attendance' : 'everyone still has time'}
              </span>} />
          </div>
        </div>

        {/* ------------------- the ones who cannot recover ------------------- */}
        {stuck.length > 0 && (
          <div className="card" style={{ padding: 16, marginBottom: 16, borderLeft: '3px solid var(--danger)' }}>
            <div className="viz-title" style={{ marginBottom: 4 }}>
              Out of time — {stuck.length} student{stuck.length === 1 ? '' : 's'}
            </div>
            <p className="viz-sub">
              Even attending every one of the {d.teachingDaysLeft} teaching days left, these students
              cannot reach {d.eligibleThreshold}% by {shortDate(d.cutOff)}. More attendance will not
              solve this; a condonation decision will, and it has to be taken while there is still
              time to document it.
            </p>
            <div style={{ display: 'grid', gap: 8 }}>
              {stuck.slice(0, 12).map((s) => (
                <div key={s.studentId} style={{
                  display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
                  padding: '9px 12px', borderRadius: 8, background: 'var(--danger-bg)',
                }}>
                  <span style={{ flex: '1 1 210px', minWidth: 0 }}>
                    <strong style={{ fontSize: 14 }}>{s.fullName}</strong>
                    <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginLeft: 6 }}>
                      {s.admissionNumber}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--ink-2)', marginLeft: 8 }}>{s.classLabel}</span>
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
                    {s.percent === null ? '—' : `${s.percent.toFixed(1)}%`}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-2)', fontVariantNumeric: 'tabular-nums' }}>
                    {s.attended}/{s.held} days
                  </span>
                  <span className="pill pill-danger" style={{ fontSize: 10 }}>
                    would need {s.daysNeeded} of {s.teachingDaysLeft}
                  </span>
                </div>
              ))}
              {stuck.length > 12 && (
                <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: 0 }}>
                  and {stuck.length - 12} more — the full list is in the table below.
                </p>
              )}
            </div>
          </div>
        )}

        {/* ---------------------- class + who to ring ---------------------- */}
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="viz-title" style={{ marginBottom: 4 }}>By class, and who to ring about it</div>
          <p className="viz-sub">
            The class teacher is the person who can explain a register and get it corrected properly.
            {canSeeContact
              ? ' Tap a number to call.'
              : ' Numbers are shown to the head of school and HR only.'}
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="viz-table" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th>Class</th>
                  <th className="num">Roll</th>
                  <th className="num">Clear</th>
                  <th className="num">Condonation</th>
                  <th className="num">Below floor</th>
                  <th className="num">Stuck</th>
                  <th className="num">Average</th>
                  <th>Class teacher</th>
                </tr>
              </thead>
              <tbody>
                {d.classes.map((c) => <ClassRow key={c.classKey} c={c} />)}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10, marginBottom: 0, lineHeight: 1.6 }}>
            ⚠ Eduvora will not let you edit a register from this screen, and that is on purpose. Boards
            inspect attendance, and once shortage cases are filed no change is accepted — so every
            correction goes through the attendance screen, where it carries a name, a reason and a
            timestamp you can show an inspector.
          </p>
        </div>

        {/* ---------------------------- watchlist ---------------------------- */}
        <div className="card" style={{ padding: 16 }}>
          <div className="viz-title" style={{ marginBottom: 4 }}>The watchlist</div>
          <p className="viz-sub">
            Everyone below {d.eligibleThreshold}%. &ldquo;Needs N more days&rdquo; assumes perfect
            attendance from tomorrow — it is the best case, not a prediction.
          </p>

          <div className="tab-strip elig-controls" style={{ borderBottom: 'none', marginBottom: 10, gap: 6 }}>
            {([['watch', `Everyone short (${d.watchlist.length})`],
               ['stuck', `Out of time (${stuck.length})`],
               ...(cie.length > 0
                 ? [['cie', `CIE shortfall (${cie.length})`] as [string, string]]   // attendance-independent
                 : [])] as Array<[string, string]>)
              .map(([k, label]) => (
                <button key={k} type="button"
                        className={'chip' + (only === k ? ' is-on' : '')}
                        onClick={() => setOnly(k as 'watch' | 'stuck' | 'cie')}>
                  {label}
                </button>
              ))}
          </div>

          {shown.length === 0 ? (
            <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: 0 }}>
              Nobody in this group — which is the answer you want.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="viz-table" style={{ minWidth: 680 }}>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Class</th>
                    <th className="num">Attendance</th>
                    <th>Standing</th>
                    <th>What it would take</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((s) => <StudentRow key={s.studentId} s={s} />)}
                </tbody>
              </table>
            </div>
          )}

          {d.cieShortfallStudents > 0 && (
            <p style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 12, marginBottom: 0, lineHeight: 1.6 }}>
              ⚠ Separately from attendance, <strong>{d.cieShortfallStudents}</strong> student(s) are below{' '}
              {d.minCiePercent}% of the CIE in at least one course. Under VTU clause 22OB 4.2(5) that
              bars them from the SEE in that course regardless of how well they have attended — a
              student can be perfectly present and still not be allowed to sit.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ClassRow({ c }: { c: EligibilityClass }) {
  return (
    <tr>
      <td>{c.classLabel}</td>
      <td className="num">{c.strength}</td>
      <td className="num">{c.eligible}</td>
      <td className="num" style={{ color: c.condonation > 0 ? 'var(--warn)' : undefined }}>
        {c.condonation || ''}
      </td>
      <td className="num" style={{ color: c.detained > 0 ? 'var(--danger)' : undefined }}>
        {c.detained || ''}
      </td>
      <td className="num" style={{ color: c.unrecoverable > 0 ? 'var(--danger)' : undefined, fontWeight: 650 }}>
        {c.unrecoverable || ''}
      </td>
      <td className="num">{c.averagePercent === null ? '—' : `${c.averagePercent.toFixed(1)}%`}</td>
      <td>
        {c.classTeacherName ? (
          <>
            {c.classTeacherName}
            {c.classTeacherDesignation && (
              <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 6 }}>
                {c.classTeacherDesignation}
              </span>
            )}
            {/* ⚠ tel: rather than a copyable string — on the phone in the principal's hand this is
                one tap, and that is the difference between a report and an action. */}
            {c.classTeacherMobile && (
              <a href={`tel:${c.classTeacherMobile}`} className="pill pill-ok"
                 style={{ fontSize: 10.5, marginLeft: 8, textDecoration: 'none' }}>
                ☎ {c.classTeacherMobile}
              </a>
            )}
            {c.classTeacherOnLeaveToday && (
              <span className="pill pill-warn" style={{ fontSize: 10, marginLeft: 6 }}>on leave today</span>
            )}
          </>
        ) : (
          <span style={{ color: 'var(--warn)' }}>no class teacher assigned</span>
        )}
      </td>
    </tr>
  );
}

function StudentRow({ s }: { s: EligibilityStudent }) {
  const style = BAND_STYLE[s.band];
  return (
    <tr>
      <td>
        {s.fullName}
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginLeft: 6 }}>
          {s.admissionNumber}
        </span>
        {s.missedExams > 0 && (
          <span className="pill pill-muted" style={{ fontSize: 10, marginLeft: 6 }}
                title="Marked absent for a paper in this window">
            missed {s.missedExams} paper{s.missedExams === 1 ? '' : 's'}
          </span>
        )}
      </td>
      <td>{s.classLabel}</td>
      <td className="num" style={{ fontWeight: 650 }}>
        {s.percent === null ? '—' : `${s.percent.toFixed(1)}%`}
        <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--ink-3)', marginLeft: 5 }}>
          {s.attended}/{s.held}
        </span>
      </td>
      <td>
        <span className={'pill ' + style.cls} style={{ fontSize: 10 }}>{style.label}</span>
        {s.cieShortfallSubjects.length > 0 && (
          <span className="pill pill-danger" style={{ fontSize: 10, marginLeft: 6 }}
                title="Below the minimum CIE to sit the SEE in these courses">
            CIE: {s.cieShortfallSubjects.join(', ')}
          </span>
        )}
      </td>
      <td style={{ color: s.canStillRecover ? 'var(--ink-2)' : 'var(--danger)' }}>{s.verdict}</td>
    </tr>
  );
}
